import React, { useRef } from 'react';
import Doodle from './Doodle';
import type { DoodleName } from './doodleArt';
import Portrait from './Portrait';
import { StickerStock, type StickerTone } from './SectionSticker';
import useCardboardTilt from './useCardboardTilt';
import './sectionSticker.css';

/**
 * A creator's photo or a brand's logo, printed on the same die-cut cardboard as a section's
 * sticker (SectionSticker): the white die margin, the ear, the kraft edge, the tilt under the
 * hand. It is the card's picture, so it has no back and does not turn: a click on it is a
 * click on the card, and the card opens.
 *
 * It is not a control. It must sit INSIDE the card's own click target (a file's <article>, a
 * row's button or link) and never handles a click itself, so the card's own open handler runs
 * once, by bubbling. The card's named control (its name button, its row) stays the one tab
 * stop and the one thing a screen reader hears; the print is aria-hidden, as the <img alt="">
 * it replaces was, because the name is printed beside it.
 *
 * `interactive` is true only where the card opens: it tilts, and takes the card's pointer. A
 * print on something that does not open lies still — motion that invites a click that does
 * nothing is a false promise.
 *
 * What is printed is the Portrait's: the photo (a logo shown whole on paper with `mark`), or
 * the initials when there is none or it fails — never a blank tile, never another brand's mark.
 * Reduced motion and a still preview hold it at rest (sectionSticker.css).
 *
 * A TOOL'S PRINT (`doodle`, founder 2026-09-24): Bearri's tool cards print their engraving on the
 * same stock — tinted paper in the starter palette's `tone`, the drawing in the tone's ink, exactly
 * a section sticker's front — because the card is ONE button and the sticker is only its picture:
 * aria-hidden, no back, never spins, tilts under the hand. Without `doodle` nothing changes.
 */
export default function PrintSticker({ name, src, mark = false, size = 'md', interactive = true, onImageFailed, className = '', doodle, tone = 'blue' }: {
  /** Whose print: the initials when there is no photo. */
  name: string;
  src?: string | null;
  /** A brand's logo: shown whole on paper, one initial when absent. */
  mark?: boolean;
  /** md = a file's picture (56px, 50px on a narrow file) · sm = a row's (44px). */
  size?: 'sm' | 'md';
  /** True only when a click on the card around it opens something. */
  interactive?: boolean;
  /** The photo failed to load (the card may fetch a fresh one); the initials show meanwhile. */
  onImageFailed?: () => void;
  className?: string;
  /** Print this engraving (a tool card's picture) instead of a photo or a logo. */
  doodle?: DoodleName;
  /** The engraving's tone, from the starter palette (with `doodle` only). */
  tone?: StickerTone;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const tilt = useCardboardTilt(ref, interactive);
  if (doodle) {
    // the section sticker's front on a print's stock: no --print (that is the neutral photo stock), so the tone reads
    return (
      <span ref={ref} className={`br-section-sticker br-section-sticker--engraved${size === 'sm' ? ' br-section-sticker--sm' : ''} ${className}`.trim()}
        data-sticker-tone={tone} data-depth={tilt.active} data-interactive={interactive} aria-hidden="true"
        {...(interactive ? tilt.handlers : {})}>
        <StickerStock surfaceRef={tilt.surfaceRef} depth={tilt.active} front={<Doodle name={doodle} size="sm" />} />
      </span>
    );
  }
  return (
    <span ref={ref} className={`br-section-sticker br-section-sticker--print br-section-sticker--${size} ${className}`.trim()}
      data-mark={mark} data-depth={tilt.active} data-interactive={interactive} aria-hidden="true"
      {...(interactive ? tilt.handlers : {})}>
      <StickerStock surfaceRef={tilt.surfaceRef} depth={tilt.active}
        front={<Portrait name={name} src={src} mark={mark} title={name} onFailed={onImageFailed} />} />
    </span>
  );
}
